import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import html2pdf from 'html2pdf.js';
import LogoLoader from './LogoLoader';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 32px 24px 120px;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 768px) {
    padding: 20px 16px 80px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;

  /* PDF print mode — hide buttons only */
  &.pdf-print-mode button {
    display: none !important;
  }
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #edf1f9;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #192947;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
    gap: 8px;
  }
`;

const GoalBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(136, 93, 232, 0.15);
  color: #7955cd;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const BackButton = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  background: #f5f8fd;
  color: #65748d;
  border: 1px solid #edf1f9;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #edf2ff;
    color: #192947;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: ${props => props.$primary ? 'rgba(136, 93, 232, 0.15)' : '#f5f8fd'};
  color: ${props => props.$primary ? '#7955cd' : '#65748d'};
  border: 1px solid ${props => props.$primary ? 'rgba(136, 93, 232, 0.2)' : '#edf1f9'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$primary ? 'rgba(136, 93, 232, 0.25)' : '#edf2ff'};
    color: ${props => props.$primary ? '#7955cd' : '#192947'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const ToastMessage = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 20px;
  border-radius: 10px;
  background: rgba(18, 165, 113, 0.15);
  border: 1px solid rgba(18, 165, 113, 0.3);
  color: #0f8f63;
  font-size: 13px;
  font-weight: 500;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => 
    props.$status === 'green' ? '#12a571' : 
    props.$status === 'yellow' ? '#ef9b61' : '#e5484d'};
  box-shadow: 0 0 8px ${props => 
    props.$status === 'green' ? 'rgba(18, 165, 113, 0.4)' : 
    props.$status === 'yellow' ? 'rgba(239, 155, 97, 0.4)' : 'rgba(229, 72, 77, 0.4)'};
`;

const StatusText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => 
    props.$status === 'green' ? '#0f8f63' : 
    props.$status === 'yellow' ? '#a2611c' : '#d63c3c'};
`;

const SubInfo = styled.span`
  font-size: 13px;
  color: #8f9db3;
  margin-left: 8px;
`;

// Summary cards row
const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const SummaryCard = styled.div`
  background: #f8faff;
  border: 1px solid #edf1f9;
  border-radius: 14px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  h4 {
    font-size: 13px;
    font-weight: 600;
    color: #7b8aa3;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  ul {
    margin: 0;
    padding-left: 16px;
    list-style: none;

    li {
      font-size: 13px;
      color: #455776;
      line-height: 1.6;
      position: relative;
      padding-left: 4px;

      &::before {
        content: '';
        position: absolute;
        left: -12px;
        top: 8px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
      }
    }
  }
`;

const BlockerList = styled.ul`
  li::before { background: #e5484d; }
`;

const RiskList = styled.ul`
  li::before { background: #ef9b61; }
`;

const NextAction = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #0f8f63;
  line-height: 1.5;
`;

// Section
const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #192947;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 18px;
    height: 18px;
    color: #7955cd;
  }
`;

// Pathway cards
const PathwaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 24px;
  }
`;

const PathwayCard = styled.div`
  background: #f8faff;
  border: 1px solid #edf1f9;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(136, 93, 232, 0.2);
    background: #f5f8fd;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 14px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const CardName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #192947;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 13px;
  }
  color: ${props => 
    props.$score >= 70 ? '#0f8f63' : 
    props.$score >= 40 ? '#a2611c' : '#d63c3c'};
  background: ${props => 
    props.$score >= 70 ? 'rgba(18, 165, 113, 0.1)' : 
    props.$score >= 40 ? 'rgba(239, 155, 97, 0.1)' : 'rgba(229, 72, 77, 0.1)'};
  border: 2px solid ${props => 
    props.$score >= 70 ? 'rgba(18, 165, 113, 0.3)' : 
    props.$score >= 40 ? 'rgba(239, 155, 97, 0.3)' : 'rgba(229, 72, 77, 0.3)'};
  flex-shrink: 0;
`;

const CardSummary = styled.p`
  font-size: 13px;
  color: #7b8aa3;
  line-height: 1.5;
  margin: 0 0 14px 0;
`;

const ConfBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  text-transform: capitalize;
  background: ${props => 
    props.$level === 'high' ? 'rgba(18, 165, 113, 0.1)' : 
    props.$level === 'medium' ? 'rgba(239, 155, 97, 0.1)' : 'rgba(229, 72, 77, 0.1)'};
  color: ${props => 
    props.$level === 'high' ? '#0f8f63' : 
    props.$level === 'medium' ? '#a2611c' : '#d63c3c'};
  margin-bottom: 14px;
  display: inline-block;
`;

const ProsCons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #edf1f9;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  h5 {
    font-size: 12px;
    font-weight: 600;
    margin: 0 0 8px 0;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      font-size: 12px;
      line-height: 1.5;
      margin-bottom: 4px;
      padding-left: 14px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 6px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }
    }
  }
`;

const ProsCol = styled.div`
  h5 { color: #0f8f63; }
  li { color: #65748d; }
  li::before { background: #12a571; }
`;

const ConsCol = styled.div`
  h5 { color: #a2611c; }
  li { color: #65748d; }
  li::before { background: #ef9b61; }
`;

// Score breakdown
const BreakdownSection = styled.div`
  background: #f8faff;
  border: 1px solid #edf1f9;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 14px;
    margin-bottom: 20px;
  }
`;

const BreakdownItem = styled.div`
  margin-bottom: 12px;

  &:last-child { margin-bottom: 0; }
`;

const BreakdownLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #455776;
  margin-bottom: 6px;

  span { 
    font-weight: 600;
    color: ${props =>
      props.$score >= 70 ? '#0f8f63' :
      props.$score >= 40 ? '#a2611c' : '#d63c3c'};
  }
`;

const ProgressBar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #f5f8fd;
  overflow: hidden;

  .fill {
    height: 100%;
    border-radius: 3px;
    width: ${props => props.$score}%;
    background: ${props =>
      props.$score >= 70 ? 'linear-gradient(90deg, #12a571, #12a571)' :
      props.$score >= 40 ? 'linear-gradient(90deg, #ef9b61, #ef9b61)' :
      'linear-gradient(90deg, #e5484d, #e5484d)'};
    transition: width 0.6s ease;
  }
`;

// Missing items
const MissingSection = styled.div`
  margin-bottom: 28px;
`;

const MissingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8faff;
  border: 1px solid #edf1f9;
  border-radius: 10px;
  margin-bottom: 8px;
`;

const PriorityBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${props =>
    props.$priority === 'blocker' ? 'rgba(229, 72, 77, 0.15)' :
    props.$priority === 'important' ? 'rgba(239, 155, 97, 0.15)' : 'rgba(101, 116, 141, 0.06)'};
  color: ${props =>
    props.$priority === 'blocker' ? '#d63c3c' :
    props.$priority === 'important' ? '#a2611c' : '#7b8aa3'};
  flex-shrink: 0;
`;

const NotesSection = styled.div`
  background: rgba(136, 93, 232, 0.04);
  border: 1px solid rgba(136, 93, 232, 0.1);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 20px;
  }
`;

const NoteItem = styled.div`
  font-size: 13px;
  color: #65748d;
  line-height: 1.6;
  padding: 8px 0;
  border-bottom: 1px solid #edf1f9;

  &:last-child { border-bottom: none; }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
  color: #455776;
`;

/** missingInformation may be plain strings, JSON strings, or { item, question, category, priority } */
function parseMissingInfoItem(raw) {
    let data = raw;
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (t.startsWith('{') && t.endsWith('}')) {
            try {
                data = JSON.parse(t);
            } catch {
                return { title: raw, subtitle: '', badgePriority: 'important', badgeLabel: 'needed' };
            }
        } else {
            return { title: raw, subtitle: '', badgePriority: 'important', badgeLabel: 'needed' };
        }
    }
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const title = data.question || data.item || data.name || data.text || '';
        const subtitle = data.category ? String(data.category) : '';
        const p = String(data.priority || '').toLowerCase();
        const badgePriority = p === 'blocker' ? 'blocker' : p === 'high' || p === 'important' ? 'important' : 'medium';
        const badgeLabel = data.priority ? String(data.priority) : 'needed';
        return {
            title: title || JSON.stringify(data),
            subtitle,
            badgePriority,
            badgeLabel
        };
    }
    return { title: String(raw ?? ''), subtitle: '', badgePriority: 'important', badgeLabel: 'needed' };
}

function escapeHtml(s) {
    if (s == null || s === '') return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// ============================================
// COMPONENT
// ============================================

const AiAnalysisView = () => {
    const { threadId } = useParams();
    const navigate = useNavigate();
    const authenticatedAxios = useAuthenticatedAxios();
    const [analysisData, setAnalysisData] = useState(null);
    const [analysisSource, setAnalysisSource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPathway, setSelectedPathway] = useState(null);
    const [savingPdf, setSavingPdf] = useState(false);
    const [toast, setToast] = useState(null);
    const reportRef = useRef(null);

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(null), 3000);
    };

    const handleSavePdf = async () => {
        if (savingPdf) return;
        setSavingPdf(true);
        try {
            const clientName = analysisData?.clientProfile?.name || 'Client';
            const filename = `AI-Case-Analysis-${clientName.replace(/\s+/g, '-')}.pdf`;

            const htmlContent = buildFullHtml(clientName);

            // Use iframe — most reliable for html2pdf
            const iframe = document.createElement('iframe');
            iframe.style.cssText = 'position:fixed;top:0;left:0;width:800px;height:600px;z-index:99999;border:none;';
            document.body.appendChild(iframe);

            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();

            // Wait for iframe to fully render
            await new Promise(resolve => {
                iframe.onload = resolve;
                setTimeout(resolve, 1000); // fallback
            });

            const target = iframeDoc.getElementById('pdf-root');

            const opt = {
                margin: [8, 6, 8, 6],
                filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            };

            await html2pdf().set(opt).from(target).save();
            document.body.removeChild(iframe);
            showToast('PDF saved successfully');
        } catch (err) {
            console.error('Failed to generate PDF:', err);
            // Remove iframe if still there
            const leftover = document.querySelector('iframe[style*="z-index:99999"]');
            if (leftover) document.body.removeChild(leftover);
            showToast('Failed to save PDF. Please try again.');
        } finally {
            setSavingPdf(false);
        }
    };

    const buildFullHtml = (clientName) => {
        // All styles inline — html2canvas captures inline styles reliably
        return `<!DOCTYPE html><html><head></head><body style="margin:0;padding:40px;background:#ffffff;color:#192947;font-family:'Inter','Segoe UI',-apple-system,BlinkMacSystemFont,Arial,sans-serif;line-height:1.5;">
        <div id="pdf-root">
            <h1 style="font-size:24px;margin:0 0 24px;color:#192947;border-bottom:2px solid #7955cd;padding-bottom:12px;">AI Case Analysis — ${clientName}</h1>
            ${buildPrintContent()}
            <div style="margin-top:32px;padding-top:16px;border-top:1px solid #edf1f9;font-size:11px;color:#7b8aa3;text-align:center;">Generated by iMaven AI &bull; ${new Date().toLocaleDateString()}</div>
        </div></body></html>`;
    };

    const buildPrintContent = () => {
        if (!analysisData) return '';
        const cp = analysisData.clientProfile || {};
        const opts = analysisData.options || [];
        const steps = analysisData.recommendedNextSteps || [];
        const docs = analysisData.documentsYouMayNeed || [];
        const questions = analysisData.questionsToConsider || [];
        const warnings = analysisData.importantWarnings || [];
        const positives = analysisData.positiveAspects || [];
        const missing = analysisData.missingInformation || [];
        const advice = analysisData.expertAdvice || '';
        const summary = analysisData.personalSummary || '';
        const readiness = analysisData.overallReadiness || 0;
        const rc = readiness >= 70 ? '#0f9a68' : readiness >= 40 ? '#d9822b' : '#dc2f2f';
        const rbg = readiness >= 70 ? '#ecfdf5' : readiness >= 40 ? '#fff7ee' : '#fff4f4';

        const profileFields = [
            cp.email && { l: 'Email', v: cp.email },
            cp.phone && { l: 'Phone', v: cp.phone },
            cp.nationality && { l: 'Nationality', v: cp.nationality },
            cp.location && { l: 'Location', v: cp.location },
            cp.destination && { l: 'Destination', v: cp.destination },
            cp.profession && { l: 'Profession', v: cp.profession },
            cp.education && { l: 'Education', v: cp.education },
            cp.maritalStatus && { l: 'Status', v: cp.maritalStatus },
            cp.familySize && { l: 'Family', v: cp.familySize },
            cp.timeline && { l: 'Timeline', v: cp.timeline },
            cp.languageTest && { l: 'Language', v: cp.languageTest },
            cp.budget && { l: 'Budget', v: cp.budget },
        ].filter(Boolean);

        let html = '';

        // Readiness badge
        html += `<div style="display:inline-block;padding:10px 20px;border-radius:10px;font-weight:700;font-size:20px;margin:8px 0;background:${rbg};color:${rc};">${readiness}% Readiness</div>`;

        // Summary
        if (summary) html += `<div style="background:#f3efff;border:1px solid #f0ecff;border-radius:10px;padding:16px;margin:16px 0;color:#192947;font-size:14px;line-height:1.6;">${summary}</div>`;

        // Profile grid
        if (profileFields.length > 0) {
            html += `<div style="margin-bottom:24px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#192947;">Your Profile (${profileFields.length} details)</h2>`;
            html += `<div style="display:flex;flex-wrap:wrap;gap:10px;">`;
            profileFields.forEach(f => {
                html += `<div style="background:#f8faff;border:1px solid #edf1f9;border-radius:8px;padding:10px 14px;flex:1 1 180px;min-width:150px;"><div style="font-size:10px;text-transform:uppercase;color:#7955cd;font-weight:700;letter-spacing:0.5px;margin-bottom:4px;">${f.l}</div><div style="font-size:13px;color:#192947;font-weight:500;">${f.v}</div></div>`;
            });
            html += `</div></div>`;
        }
        if (cp.keyFact) html += `<div style="background:#f3efff;border:1px solid #f0ecff;border-radius:8px;padding:12px;margin:12px 0;color:#4c1d95;font-size:13px;"><strong style="color:#7955cd;">Key Fact:</strong> ${cp.keyFact}</div>`;

        // Positives
        if (positives.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#0f8f63;">What's Working in Your Favor</h2>`;
            positives.forEach(p => { html += `<div style="color:#0f8f63;font-size:13px;margin:6px 0;padding:6px 0;">&#10003; ${p}</div>`; });
            html += `</div>`;
        }

        // Warnings
        if (warnings.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#c53030;">Important Things to Know</h2>`;
            warnings.forEach(w => { html += `<div style="background:#fff4f4;border:1px solid #f1c6c6;border-radius:8px;padding:10px 14px;margin:6px 0;color:#991b1b;font-size:13px;">&#9888; ${w}</div>`; });
            html += `</div>`;
        }

        // Options
        if (opts.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#192947;">Your Options (${opts.length})</h2>`;
            opts.forEach(opt => {
                const s = opt.score || 0;
                const sc = s >= 70 ? '#0f9a68' : s >= 40 ? '#d9822b' : '#dc2f2f';
                const sbg = s >= 70 ? '#ecfdf5' : s >= 40 ? '#fff7ee' : '#fff4f4';
                html += `<div style="background:#f8faff;border:1px solid #edf1f9;border-radius:10px;padding:16px;margin:12px 0;">`;
                html += `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;"><span style="font-size:16px;font-weight:600;color:#192947;">${opt.name || ''}</span><span style="font-size:16px;font-weight:700;padding:6px 14px;border-radius:20px;background:${sbg};color:${sc};">${s}</span></div>`;
                html += `<p style="font-size:13px;color:#65748d;margin:0 0 8px;">${opt.description || opt.summary || ''}</p>`;
                if (opt.confidence) html += `<p style="font-size:12px;color:#7955cd;margin:0 0 8px;">Confidence: ${opt.confidence}</p>`;
                if (opt.estimatedTimeline || opt.estimatedCost) {
                    html += `<p style="font-size:12px;color:#65748d;margin:0 0 8px;">`;
                    if (opt.estimatedTimeline) html += `&#9200; ${opt.estimatedTimeline} `;
                    if (opt.estimatedCost) html += `&#128176; ${opt.estimatedCost}`;
                    html += `</p>`;
                }
                const pros = opt.whyThisWorks || opt.pros || [];
                const cons = opt.challenges || opt.cons || [];
                if (pros.length || cons.length) {
                    html += `<div style="display:flex;gap:16px;margin-top:12px;padding-top:12px;border-top:1px solid #edf1f9;">`;
                    html += `<div style="flex:1;"><h4 style="color:#0f8f63;font-size:12px;text-transform:uppercase;margin:0 0 6px;">Why This Could Work</h4><ul style="padding-left:16px;margin:0;">`;
                    pros.forEach(p => { html += `<li style="font-size:12px;color:#192947;margin-bottom:3px;">${p}</li>`; });
                    html += `</ul></div>`;
                    html += `<div style="flex:1;"><h4 style="color:#98601e;font-size:12px;text-transform:uppercase;margin:0 0 6px;">Challenges</h4><ul style="padding-left:16px;margin:0;">`;
                    cons.forEach(c => { html += `<li style="font-size:12px;color:#192947;margin-bottom:3px;">${c}</li>`; });
                    html += `</ul></div></div>`;
                }
                if (opt.breakdown?.length) {
                    html += `<div style="margin-top:12px;padding-top:12px;border-top:1px solid #edf1f9;">`;
                    opt.breakdown.forEach(b => {
                        const bc = b.score >= 70 ? '#0f9a68' : b.score >= 40 ? '#d9822b' : '#dc2f2f';
                        html += `<div style="margin-bottom:8px;"><div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:3px;"><span style="color:#192947;">${b.name}</span><span style="color:${bc};font-weight:600;">${b.score}%</span></div><div style="height:6px;background:#e0e8f5;border-radius:3px;overflow:hidden;"><div style="height:100%;width:${b.score}%;background:${bc};border-radius:3px;"></div></div></div>`;
                    });
                    html += `</div>`;
                }
                html += `</div>`;
            });
            html += `</div>`;
        }

        // Next Steps
        if (steps.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#192947;">Recommended Next Steps</h2>`;
            steps.forEach(s => {
                const step = typeof s === 'string' ? s : s.step || s.action || '';
                const priority = s.priority || 'medium';
                const pc = priority === 'high' ? '#dc2f2f' : priority === 'low' ? '#0f9a68' : '#d9822b';
                const pbg = priority === 'high' ? '#fff4f4' : priority === 'low' ? '#f0fdf4' : '#fff7ee';
                html += `<div style="background:#f8faff;border:1px solid #edf1f9;border-radius:8px;padding:10px 14px;margin:6px 0;display:flex;gap:10px;align-items:flex-start;"><span style="font-size:10px;text-transform:uppercase;font-weight:700;padding:2px 8px;border-radius:4px;white-space:nowrap;background:${pbg};color:${pc};">${priority}</span><div><strong style="font-size:13px;color:#192947;">${step}</strong>${s.why ? `<br><span style="font-size:12px;color:#65748d;">${s.why}</span>` : ''}${s.timeline ? `<br><span style="font-size:11px;color:#7b8aa3;">${s.timeline}</span>` : ''}</div></div>`;
            });
            html += `</div>`;
        }

        // Documents
        if (docs.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#192947;">Documents You May Need</h2><ul style="padding-left:20px;columns:2;margin:8px 0;">`;
            docs.forEach(d => { html += `<li style="font-size:13px;color:#192947;margin-bottom:4px;">${d}</li>`; });
            html += `</ul></div>`;
        }

        // Questions
        if (questions.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#192947;">Questions to Consider</h2><ul style="padding-left:20px;columns:2;margin:8px 0;">`;
            questions.forEach(q => { html += `<li style="font-size:13px;color:#192947;margin-bottom:4px;">${q}</li>`; });
            html += `</ul></div>`;
        }

        // Missing info
        if (missing.length > 0) {
            html += `<div style="margin-bottom:20px;"><h2 style="font-size:18px;margin:24px 0 12px;color:#192947;">Information That Would Help</h2><ul style="padding-left:20px;margin:8px 0;">`;
            missing.forEach((m) => {
                const { title, subtitle } = parseMissingInfoItem(m);
                const sub = subtitle ? `<div style="font-size:11px;color:#65748d;margin-top:4px;">${escapeHtml(subtitle)}</div>` : '';
                html += `<li style="font-size:13px;color:#192947;margin-bottom:8px;"><strong>${escapeHtml(title)}</strong>${sub}</li>`;
            });
            html += `</ul></div>`;
        }

        // Expert advice
        if (advice) {
            html += `<div style="background:#f3efff;border:1px solid #f0ecff;border-radius:8px;padding:14px;margin-top:20px;color:#4c1d95;font-size:14px;"><strong style="color:#7955cd;">Why Consult with ${analysisData._metadata?.expertName || 'an Expert'}?</strong><br>${advice}</div>`;
        }

        return html;
    };

    const handleShare = async () => {
        const clientName = analysisData?.clientProfile?.name || 'Client';
        const url = window.location.href;
        const title = `AI Case Analysis - ${clientName}`;

        // Try native share with link
        if (navigator.share) {
            try {
                await navigator.share({ title, text: `AI Case Analysis for ${clientName}`, url });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    await copyToClipboard(url);
                }
            }
        } else {
            await copyToClipboard(url);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast('Link copied to clipboard');
        } catch {
            showToast('Could not copy link');
        }
    };

    useEffect(() => {
        const fetchAnalysis = async () => {
            const base = process.env.REACT_APP_SERVER || '';
            console.info('[UserPlatform/Analysis] GET', `${base}/api/analysis/${threadId}`);
            try {
                const response = await authenticatedAxios.get(`/api/analysis/${threadId}`);
                const data = response.data?.analysis || response.data?.lastAnalysis?.data || response.data;
                setAnalysisSource(response.data?.source || null);
                console.info('[UserPlatform/Analysis] GET OK', { source: response.data?.source, keys: Object.keys(data || {}) });
                setAnalysisData(data);
            } catch (err) {
                const msg = err.response?.data?.message || err.response?.data?.error;
                console.error('[UserPlatform/Analysis] GET FAILED', err.response?.status, err.response?.data || err.message);
                setError(
                    msg || 'Analysis not found or still processing. If you only finished the widget chat, wait a minute and refresh, or run “AI analysis” from the app again.'
                );
            } finally {
                setLoading(false);
            }
        };

        if (threadId) {
            fetchAnalysis();
        }
    }, [threadId, authenticatedAxios]);

    if (loading) {
        return (
            <PageContainer>
                <LoadingContainer>
                    <LogoLoader size={180} message="Loading your analysis" />
                </LoadingContainer>
            </PageContainer>
        );
    }

    if (error || !analysisData) {
        return (
            <PageContainer>
                <LoadingContainer>
                    <p style={{ color: '#d63c3c' }}>{error || 'No analysis data available.'}</p>
                    <BackButton onClick={() => navigate(-1)}>Go Back</BackButton>
                </LoadingContainer>
            </PageContainer>
        );
    }

    // Extract user-analysis fields (user-focused structure)
    const clientProfile = analysisData.clientProfile || {};
    const personalSummary = analysisData.personalSummary || '';
    const options = analysisData.options || analysisData.pathways || [];
    const recommendedNextSteps = analysisData.recommendedNextSteps || [];
    const documentsYouMayNeed = analysisData.documentsYouMayNeed || [];
    const questionsToConsider = analysisData.questionsToConsider || [];
    const importantWarnings = analysisData.importantWarnings || [];
    const positiveAspects = analysisData.positiveAspects || [];
    const missingInformation = analysisData.missingInformation || [];
    const expertAdvice = analysisData.expertAdvice || '';
    const expertName = analysisData._metadata?.expertName || '';
    const clientName = clientProfile.name || 'Client';

    // Overall readiness
    const overallReadiness = analysisData.overallReadiness || 
        (options.length > 0 ? options.reduce((acc, opt) => acc + (opt.score || 0), 0) / options.length : 0);
    const overallStatus = analysisData.statusLabel || (overallReadiness >= 70 ? 'green' : overallReadiness >= 40 ? 'yellow' : 'red');
    const statusLabel = overallReadiness >= 70 ? 'Good Standing' : overallReadiness >= 40 ? 'Needs Attention' : 'Needs More Information';

    const getCountryFlag = (country) => {
        const flags = { 'Canada': '\u{1F1E8}\u{1F1E6}', 'USA': '\u{1F1FA}\u{1F1F8}', 'UK': '\u{1F1EC}\u{1F1E7}', 'Australia': '\u{1F1E6}\u{1F1FA}', 'Germany': '\u{1F1E9}\u{1F1EA}', 'France': '\u{1F1EB}\u{1F1F7}', 'Israel': '\u{1F1EE}\u{1F1F1}' };
        return flags[country] || '\u{1F30D}';
    };

    return (
        <PageContainer>
            <ContentWrapper ref={reportRef}>
                {/* Header */}
                <Header>
                    <div>
                        <HeaderTitle>
                            Your AI Case Overview
                        </HeaderTitle>
                        {analysisSource === 'lastAnalysis' && (
                            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#7b8aa3', maxWidth: '520px' }}>
                                Showing the same case analysis prepared for your adviser after your chat. For a separate client-friendly summary, use &ldquo;AI analysis&rdquo; in the app when available.
                            </p>
                        )}
                    </div>
                    <HeaderActions>
                        <ActionBtn onClick={handleSavePdf} disabled={savingPdf} $primary>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            {savingPdf ? 'Saving...' : 'Save PDF'}
                        </ActionBtn>
                        <ActionBtn onClick={handleShare}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                            Share
                        </ActionBtn>
                        <BackButton onClick={() => navigate(-1)}>
                            &larr; Back
                        </BackButton>
                    </HeaderActions>
                </Header>

                {/* Personal Profile Card */}
                <div style={{
                    background: 'linear-gradient(135deg, rgba(136, 93, 232, 0.08) 0%, rgba(45, 91, 237, 0.06) 100%)',
                    border: '1px solid rgba(136, 93, 232, 0.15)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '24px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Accent line */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                        background: 'linear-gradient(90deg, #7955cd, #2d5bed, transparent)'
                    }} />

                    {/* Name and goal */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <h2 style={{ 
                                fontSize: '20px', fontWeight: '700', color: '#192947', 
                                margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                                </svg>
                                {clientName}
                            </h2>
                            {clientProfile.goal && (
                                <p style={{ fontSize: '13px', color: '#7b8aa3', margin: 0 }}>
                                    {clientProfile.goal}
                                </p>
                            )}
                        </div>
                        {/* Readiness score badge */}
                        <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            background: overallStatus === 'green' ? 'rgba(18, 165, 113, 0.1)' : overallStatus === 'yellow' ? 'rgba(239, 155, 97, 0.1)' : 'rgba(229, 72, 77, 0.1)',
                            border: `1px solid ${overallStatus === 'green' ? 'rgba(18, 165, 113, 0.2)' : overallStatus === 'yellow' ? 'rgba(239, 155, 97, 0.2)' : 'rgba(229, 72, 77, 0.2)'}`,
                            borderRadius: '12px', padding: '10px 16px', minWidth: '80px'
                        }}>
                            <span style={{ 
                                fontSize: '22px', fontWeight: '700',
                                color: overallStatus === 'green' ? '#0f8f63' : overallStatus === 'yellow' ? '#a2611c' : '#d63c3c'
                            }}>
                                {Math.round(overallReadiness)}%
                            </span>
                            <span style={{ fontSize: '10px', color: '#8f9db3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Readiness
                            </span>
                        </div>
                    </div>

                    {/* Profile details grid */}
                    {(() => {
                        const profileItems = [
                            clientProfile.email && { label: 'Email', value: clientProfile.email, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg> },
                            clientProfile.phone && { label: 'Phone', value: clientProfile.phone, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg> },
                            clientProfile.nationality && { label: 'Nationality', value: clientProfile.nationality, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
                            clientProfile.location && { label: 'Location', value: clientProfile.location, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                            clientProfile.destination && { label: 'Destination', value: clientProfile.destination, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg> },
                            clientProfile.profession && { label: 'Profession', value: clientProfile.profession, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg> },
                            clientProfile.education && { label: 'Education', value: clientProfile.education, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5"/></svg> },
                            clientProfile.maritalStatus && { label: 'Status', value: clientProfile.maritalStatus, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
                            clientProfile.familySize && { label: 'Family', value: clientProfile.familySize, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
                            clientProfile.timeline && { label: 'Timeline', value: clientProfile.timeline, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
                            clientProfile.languageTest && { label: 'Language', value: clientProfile.languageTest, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg> },
                            clientProfile.budget && { label: 'Budget', value: clientProfile.budget, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8f9db3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
                        ].filter(Boolean);
                        
                        if (profileItems.length === 0) return null;
                        
                        const fieldsCollected = profileItems.length;
                        
                        return (
                            <div style={{ paddingTop: '14px', borderTop: '1px solid #edf1f9' }}>
                                <div style={{ fontSize: '11px', color: '#a9b5c8', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {fieldsCollected} details collected from your conversation
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                                    {profileItems.map((item, i) => (
                                        <div key={i} style={{ 
                                            background: '#f8faff',
                                            border: '1px solid #edf1f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '6px'
                                        }}>
                                            <div style={{ 
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                fontSize: '11px', color: '#8f9db3',
                                                textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600
                                            }}>
                                                {item.icon}
                                                {item.label}
                                            </div>
                                            <div style={{ 
                                                fontSize: '13px', color: '#33445e',
                                                fontWeight: 500, lineHeight: '1.4'
                                            }}>
                                                {item.value}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {clientProfile.keyFact && (
                                    <div style={{ 
                                        display: 'flex', alignItems: 'center', gap: '8px', 
                                        marginTop: '12px', padding: '10px 14px', 
                                        background: 'rgba(136, 93, 232, 0.08)', 
                                        border: '1px solid rgba(136, 93, 232, 0.15)',
                                        borderRadius: '10px',
                                        fontSize: '13px', color: '#7955cd'
                                    }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                        {clientProfile.keyFact}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Summary text */}
                    {personalSummary && (
                        <p style={{ 
                            fontSize: '14px', color: '#455776', 
                            lineHeight: '1.6', margin: '16px 0 0 0',
                            paddingTop: '14px', borderTop: '1px solid #edf1f9'
                        }}>
                            {personalSummary}
                        </p>
                    )}
                </div>

                {/* Status row */}
                <StatusRow>
                    <StatusDot $status={overallStatus} />
                    <StatusText $status={overallStatus}>{statusLabel}</StatusText>
                    <SubInfo>
                        {options.length} option{options.length !== 1 ? 's' : ''} identified
                    </SubInfo>
                </StatusRow>

                {/* Positive Aspects */}
                {positiveAspects.length > 0 && (
                    <SummaryRow>
                        <SummaryCard style={{ gridColumn: '1 / -1' }}>
                            <h4 style={{ color: '#0f8f63' }}>What's Working in Your Favor</h4>
                            <ul style={{ margin: 0, paddingLeft: '16px', listStyle: 'none' }}>
                                {positiveAspects.map((item, i) => (
                                    <li key={i} style={{ 
                                        fontSize: '14px', color: '#455776', 
                                        lineHeight: '1.7', paddingLeft: '4px', position: 'relative',
                                        marginBottom: '6px'
                                    }}>
                                        <span style={{ color: '#0f8f63', marginRight: '8px' }}>&#10003;</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </SummaryCard>
                    </SummaryRow>
                )}

                {/* Important Warnings */}
                {importantWarnings.length > 0 && (
                    <SummaryRow>
                        <SummaryCard style={{ gridColumn: '1 / -1', borderColor: 'rgba(229, 72, 77, 0.2)' }}>
                            <h4 style={{ color: '#d63c3c' }}>Important Things to Know</h4>
                            <BlockerList>
                                {importantWarnings.map((w, i) => <li key={i}>{w}</li>)}
                            </BlockerList>
                        </SummaryCard>
                    </SummaryRow>
                )}

                {/* Your Options */}
                {options.length > 0 && (
                    <>
                        <SectionTitle>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                            </svg>
                            Your Options
                        </SectionTitle>

                        <PathwaysGrid>
                            {options.map((option, idx) => (
                                <PathwayCard key={idx} onClick={() => setSelectedPathway(
                                    selectedPathway?.name === option.name ? null : option
                                )} style={{
                                    borderColor: selectedPathway?.name === option.name ? 'rgba(136, 93, 232, 0.3)' : undefined
                                }}>
                                    <CardHeader>
                                        <CardName>{option.name}</CardName>
                                        <ScoreCircle $score={option.score || 0}>
                                            {option.score || 0}
                                        </ScoreCircle>
                                    </CardHeader>

                                    <CardSummary>{option.description || option.summary}</CardSummary>

                                    {option.confidence && (
                                        <ConfBadge $level={option.confidence}>
                                            Confidence: {option.confidence}
                                        </ConfBadge>
                                    )}

                                    {(option.estimatedTimeline || option.estimatedCost) && (
                                        <div style={{ 
                                            display: 'flex', gap: '16px', marginBottom: '12px',
                                            fontSize: '12px', color: '#7b8aa3' 
                                        }}>
                                            {option.estimatedTimeline && <span>&#9200; {option.estimatedTimeline}</span>}
                                            {option.estimatedCost && <span>&#128176; {option.estimatedCost}</span>}
                                        </div>
                                    )}

                                    <ProsCons>
                                        <ProsCol>
                                            <h5>Why This Could Work</h5>
                                            <ul>
                                                {(option.whyThisWorks || option.pros)?.slice(0, 3).map((p, i) => <li key={i}>{p}</li>)}
                                            </ul>
                                        </ProsCol>
                                        <ConsCol>
                                            <h5>Challenges</h5>
                                            <ul>
                                                {(option.challenges || option.cons)?.slice(0, 3).map((c, i) => <li key={i}>{c}</li>)}
                                            </ul>
                                        </ConsCol>
                                    </ProsCons>
                                </PathwayCard>
                            ))}
                        </PathwaysGrid>
                    </>
                )}

                {/* Recommended Next Steps */}
                {recommendedNextSteps.length > 0 && (
                    <MissingSection>
                        <SectionTitle>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                            Recommended Next Steps
                        </SectionTitle>
                        {recommendedNextSteps.map((item, idx) => (
                            <MissingItem key={idx}>
                                <PriorityBadge $priority={item.priority || 'medium'}>{item.priority || 'step'}</PriorityBadge>
                                <div style={{ flex: 1 }}>
                                    <span style={{ fontSize: '13px', color: '#33445e', fontWeight: 500 }}>
                                        {typeof item === 'string' ? item : item.step || item.action || JSON.stringify(item)}
                                    </span>
                                    {item.why && (
                                        <div style={{ fontSize: '12px', color: '#8f9db3', marginTop: '4px' }}>
                                            {item.why}
                                        </div>
                                    )}
                                </div>
                            </MissingItem>
                        ))}
                    </MissingSection>
                )}

                {/* Documents & Questions */}
                {(documentsYouMayNeed.length > 0 || questionsToConsider.length > 0) && (
                    <SummaryRow>
                        {documentsYouMayNeed.length > 0 && (
                            <SummaryCard>
                                <h4>Documents You May Need</h4>
                                <ul style={{ margin: 0, paddingLeft: '16px', listStyle: 'none' }}>
                                    {documentsYouMayNeed.map((doc, i) => (
                                        <li key={i} style={{ 
                                            fontSize: '13px', color: '#455776', 
                                            lineHeight: '1.7', marginBottom: '4px' 
                                        }}>
                                            <span style={{ color: '#7955cd', marginRight: '8px' }}>&#128196;</span>
                                            {doc}
                                        </li>
                                    ))}
                                </ul>
                            </SummaryCard>
                        )}
                        {questionsToConsider.length > 0 && (
                            <SummaryCard>
                                <h4>Questions to Think About</h4>
                                <ul style={{ margin: 0, paddingLeft: '16px', listStyle: 'none' }}>
                                    {questionsToConsider.map((q, i) => (
                                        <li key={i} style={{ 
                                            fontSize: '13px', color: '#455776', 
                                            lineHeight: '1.7', marginBottom: '4px' 
                                        }}>
                                            <span style={{ color: '#a2611c', marginRight: '8px' }}>&#10067;</span>
                                            {q}
                                        </li>
                                    ))}
                                </ul>
                            </SummaryCard>
                        )}
                    </SummaryRow>
                )}

                {/* Missing Information */}
                {missingInformation.length > 0 && (
                    <MissingSection>
                        <SectionTitle>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                                <rect x="9" y="3" width="6" height="4" rx="2" />
                                <path d="M9 14l2 2 4-4" />
                            </svg>
                            Information That Would Help
                        </SectionTitle>
                        {missingInformation.map((item, idx) => {
                            const parsed = parseMissingInfoItem(item);
                            return (
                                <MissingItem key={idx}>
                                    <PriorityBadge $priority={parsed.badgePriority}>{parsed.badgeLabel}</PriorityBadge>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', color: '#33445e', lineHeight: 1.5 }}>
                                            {parsed.title}
                                        </div>
                                        {parsed.subtitle ? (
                                            <div style={{ fontSize: '11px', color: '#8f9db3', marginTop: '4px' }}>
                                                {parsed.subtitle}
                                            </div>
                                        ) : null}
                                    </div>
                                </MissingItem>
                            );
                        })}
                    </MissingSection>
                )}

                {/* Expert Advice */}
                {expertAdvice && (
                    <NotesSection>
                        <SectionTitle>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                            Why Consult with {expertName || 'an Expert'}?
                        </SectionTitle>
                        <NoteItem style={{ borderBottom: 'none' }}>{expertAdvice}</NoteItem>
                    </NotesSection>
                )}
            </ContentWrapper>
            {toast && <ToastMessage>{toast}</ToastMessage>}
        </PageContainer>
    );
};

export default AiAnalysisView;
